import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { CatalogEntityPagePermissionsProvider } from '../catalog/catalogEntityPagePermissionsContext';
import { TechDocsShadowEditGate } from '../techdocs/TechDocsShadowEditGate';
import {
  AdminGroupMemberProvider,
  DevToolsRouteGuard,
  useAdminGroupMember,
} from '../devtools/AdminGroupMemberContext';
import { CatalogEntityKindAccessGate } from '../catalog/CatalogEntityKindAccessGate';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { IronPhoenixMyGroupsSidebarItem } from './IronPhoenixMyGroupsSidebarItem';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import BuildIcon from '@material-ui/icons/Build';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import ListAltIcon from '@material-ui/icons/ListAlt';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';

const useSidebarLogoStyles = makeStyles({
  root: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    minHeight: 3 * sidebarConfig.logoHeight,
    marginBottom: -14,
    boxSizing: 'border-box',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    marginLeft: 24,
    marginRight: 8,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const drawerWidth = isOpen
    ? sidebarConfig.drawerWidthOpen
    : sidebarConfig.drawerWidthClosed;

  return (
    <div className={classes.root} style={{ width: drawerWidth, maxWidth: '100%' }}>
      <Link
        to="/"
        underline="none"
        className={classes.link}
        style={{ width: isOpen ? '100%' : sidebarConfig.drawerWidthClosed }}
        aria-label="Home"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

/** Sidebar entry for admin-only scaffolder log lookup by request number (`/requests/ticket-logs`). */
function TicketLogsSidebarItem() {
  const admin = useAdminGroupMember();
  if (admin !== true) return null;
  return (
    <SidebarItem icon={ListAltIcon} to="/requests/ticket-logs" text="Ticket logs" />
  );
}

/** Admin-only CLI service accounts (`/service-accounts`). */
function ServiceAccountsSidebarItem() {
  const admin = useAdminGroupMember();
  if (admin !== true) return null;
  return (
    <SidebarItem icon={VpnKeyIcon} to="/service-accounts" text="Service Accounts" />
  );
}

/** Admin-only user list + CLI token revoke (`/user-management`). */
function UserManagementSidebarItem() {
  const admin = useAdminGroupMember();
  if (admin !== true) return null;
  return (
    <SidebarItem
      icon={PeopleAltIcon}
      to="/user-management"
      text="User Management"
    />
  );
}

function MainSidebar() {
  const devToolsAllowed = useAdminGroupMember();
  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/catalog" text="Home" />
        <UserManagementSidebarItem />
        <ServiceAccountsSidebarItem />
        <IronPhoenixMyGroupsSidebarItem title="My Groups" icon={GroupIcon} />
        {devToolsAllowed === true && (
          <SidebarItem icon={ExtensionIcon} to="/api-docs" text="APIs" />
        )}
        <SidebarItem icon={LibraryBooks} to="/docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="/create" text="Create Requests" />
        <SidebarItem icon={InfoOutlinedIcon} to="/system-info" text="System Info" />
        <SidebarItem icon={ConfirmationNumberIcon} to="/requests" text="Requests" />
        <TicketLogsSidebarItem />
        {devToolsAllowed === true && (
          <SidebarItem icon={BuildIcon} to="/devtools" text="DevTools" />
        )}
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
  );
}

function AppShell({ children }: PropsWithChildren<{}>) {
  const { pathname } = useLocation();
  // CLI OAuth consent page: no sidebar chrome (Home, Docs, Settings, …).
  const isCliLogin = pathname.startsWith('/cli-login');

  const gated = (
    <CatalogEntityPagePermissionsProvider>
      <TechDocsShadowEditGate />
      {isCliLogin ? null : <MainSidebar />}
      <CatalogEntityKindAccessGate>
        <DevToolsRouteGuard>{children}</DevToolsRouteGuard>
      </CatalogEntityKindAccessGate>
    </CatalogEntityPagePermissionsProvider>
  );

  if (isCliLogin) {
    return gated;
  }

  return <SidebarPage>{gated}</SidebarPage>;
}

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <AdminGroupMemberProvider>
    <AppShell>{children}</AppShell>
  </AdminGroupMemberProvider>
);
